import { Client, IFrame, IMessage, StompSubscription } from '@stomp/stompjs';
import { PluginType } from '../../components/plugins/types';

import { getCurrentUser } from '../../utils/accountHelper';
import { isSSR } from '../../utils/helpers';
import { getLocalStorageItem, StorageHelperKeys } from '../../utils/storageHelpers';
import { getPluginStore, getSecurityStore } from '../../utils/store/getStore';
import { getChatPlugin } from '../../utils/store/pluginStore';
import uuid from '../../utils/uuid';
import { InternalEventType, SERVER_MESSAGE_CODE, websocketConnectionReadyEvent, WsMessage } from './types';

interface ChatSubscriptionOptions {
    brokerUrl: string;
    chatSessionId: string;
    tenantId: string;
    token: string;
}
type ActivateConnectionOptions = Pick<ChatSubscriptionOptions, 'brokerUrl' | 'token'> & {
    destination: string;
};
/**
 * msgGrispiId or message id header
 */
const MESSAGE_ID_HEADER = 'msg-grispi-id';
const SENDER_SESSOIN_ID_HEADER = 'sender-session-id';
const UPDATES_QUEUE_DESTINATION = `/user/queue/updates`;
const RECONNECT_ATTEMPT_LIMIT = 3;
const WELCOME_MESSAGE_SENDER_ID = -1;

/**
 * used in incomingMessageHandler to decide whether to send message to related tab or ChatMessagesUpdates component
 * used in ensureWsSubscriptions to ensure subscriptions
 */
let chatSessionIdsOfOpenTabs: string[] = [];
// let chatSessionIdOnActiveTab: string | undefined;

function addTabRelatedChatSessionId(chatSessionId: string) {
    chatSessionIdsOfOpenTabs = [...new Set([...chatSessionIdsOfOpenTabs, chatSessionId])];
}
function removeTabRelatedChatSessionId(chatSessionId: string) {
    chatSessionIdsOfOpenTabs = chatSessionIdsOfOpenTabs.filter((id) => id !== chatSessionId);
}
function parseChatSessionIdFromSubscription(destination: string) {
    return destination?.split('.')[1];
}

function isMessageMine(message: WsMessage): boolean {
    return message.senderId === getCurrentUser().id;
}

function isMessageSentByMe(message: WsMessage): boolean {
    return isMessageMine(message);
}

function getBrokerUrl() {
    return getChatPlugin()?.manifest?.src;
}
function getTenantId() {
    return getSecurityStore()?.tenantId;
}
function getToken() {
    return getSecurityStore()?.accessToken;
}
function sendSeen(messageId: string, chatSessionId: string) {
    if (client && client.connected) {
        client?.publish({
            destination: `/app/chat/${getTenantId()}/${chatSessionId}/seen`,
            headers: {},
            body: JSON.stringify({ seenAt: Date.now(), msgGrispiId: messageId })
        });
    } else {
        console.debug('connection.ts sendSeen : client not exists or not connected yet!', client);
    }
}

const chatDisconnectedEvent = (chatSessionId: string = null) => {
    window.dispatchEvent(
        new CustomEvent(
            chatSessionId
                ? `${InternalEventType.CHAT_DISCONNECTED}-${chatSessionId}`
                : InternalEventType.CHAT_DISCONNECTED
        )
    );
};
const myMessageSentEvent = (receiptId: string, chatSessionId: string) => {
    window.dispatchEvent(new CustomEvent(`${InternalEventType.GOT_RECEIPT}-${chatSessionId}`, { detail: receiptId }));
};
const myMessageReceivedEvent = (messageId: string, chatSessionId: string) => {
    window.dispatchEvent(
        new CustomEvent(`${InternalEventType.MESSAGE_RECEIVED}-${chatSessionId}`, { detail: messageId })
    );
};
const myMessageSeenEvent = (messageId: string, seenAt: number, chatSessionId: string) => {
    window.dispatchEvent(
        new CustomEvent(`${InternalEventType.MESSAGE_SEEN}-${chatSessionId}`, { detail: { messageId, seenAt } })
    );
};

const WebSocketErrorMessages = {
    tokenIsMissing: 'TOKEN_IS_MISSING',
    tokenExpired: 'TOKEN_EXPIRED',
    unexpectedError: 'UNEXPECTED_ERROR'
} as const;

let subscriptions: Record<string, StompSubscription> = {};
let subscriptionToUpdates: StompSubscription | undefined;
let reconnectAttemptCount = 0;

const unSubscribeFromSubscription = (destination: string) => {
    if (subscriptions[destination]) {
        subscriptions[destination].unsubscribe();
        delete subscriptions[destination];
        removeTabRelatedChatSessionId(parseChatSessionIdFromSubscription(destination));
    }

    !getLocalStorageItem(StorageHelperKeys.LOGGED_OUT) &&
        chatDisconnectedEvent(parseChatSessionIdFromSubscription(destination));
};

const unSubscribeFromAllSubscriptions = (unSubscribeFromUpdates = true) => {
    Object.keys(subscriptions).forEach(unSubscribeFromSubscription);

    if (unSubscribeFromUpdates) {
        subscriptionToUpdates?.unsubscribe();
        subscriptionToUpdates = undefined;
    }
};
const unSubscribeFromSubscriptionByChatSessionId = (chatSessionId: string) => {
    Object.entries(subscriptions).forEach(([destination, _]) => {
        if (destination.includes(chatSessionId)) {
            unSubscribeFromSubscription(destination);
        }
    });
};

const client = new Client({
    onWebSocketClose: (_frame: IFrame) => {
        if (getLocalStorageItem(StorageHelperKeys.LOGGED_OUT)) return;

        const chatPluginId = getChatPlugin()?.pluginId;
        getPluginStore().setPluginIsInitializing(chatPluginId, PluginType.CHAT, true);

        if (reconnectAttemptCount < RECONNECT_ATTEMPT_LIMIT) {
            reconnectAttemptCount = reconnectAttemptCount + 1;
            startWSConnection(getBrokerUrl(), getToken());
            return;
        }
        getPluginStore().setChatPluginError(chatPluginId, 'initializingError');

        unSubscribeFromAllSubscriptions();
        client.deactivate();
        chatDisconnectedEvent();
        reconnectAttemptCount = 0;
    },
    onDisconnect: (_frame: IFrame) => {
        unSubscribeFromAllSubscriptions();
        client.deactivate();
        chatDisconnectedEvent();
    },
    onStompError: (frame: IFrame) => {
        if ('message' in frame.headers) {
            const message = frame.headers['message'];
            switch (message) {
                case WebSocketErrorMessages.tokenExpired:
                    console.error('WebSocket client onWebSocketClose token has expired');
                    break;
                case WebSocketErrorMessages.tokenIsMissing:
                    console.error('WebSocket client onWebSocketClose there is no token');
                    break;
                case WebSocketErrorMessages.unexpectedError:
                    console.error('WebSocket client onWebSocketClose unexpected Error', frame);
                    break;
                default:
                    console.error('WebSocket client onWebSocketClose Stomp Error :', message, frame);
            }
        }
        unSubscribeFromAllSubscriptions();
        //If there is an Error disconnect from websocket, if end user types we will connect again.
        chatDisconnectedEvent(); //This might not be necessary. Maybe when there's an error onDisconnect or onWebsocketClose already called.
    },
    debug: (_str) => {
        return;
    }
});

const startWSConnection = async (brokerUrl: string, token: string = undefined): Promise<void> => {
    if (isSSR()) return;

    if (!brokerUrl) {
        console.error('startWSConnection, brokerUrl is not defined');
        getPluginStore().setChatPluginError(getChatPlugin()?.pluginId, 'initializingError');
        return;
    }
    // start WS Connection with a subscription to `/user/queue/updates` destination
    await activateConnection({
        brokerUrl,
        destination: UPDATES_QUEUE_DESTINATION,
        token: token ?? getToken()
    });
};

const restartWSConnectionWithRefreshedToken = (refreshedAccesToken: string): void => {
    const brokerUrl = getChatPlugin()?.manifest?.src;

    if (!brokerUrl) {
        getPluginStore().setChatPluginError(getChatPlugin()?.pluginId, 'initializingError');
        return;
    }
    unSubscribeFromAllSubscriptions();
    client.deactivate().then(() => {
        chatDisconnectedEvent();
        startWSConnection(brokerUrl, refreshedAccesToken);
    });
};

/*
Async activateConnection function takes destination (destination to subscribe) , brokerUrl (websocker Url), token (get after calling creating a chat or resuming a chat) , endUserName.
Creates Connection to Websocket through Stomp , subscribes to destination and starts listening.
Every websocket message processed and sends internal events to react application.
*/
const activateConnection = async ({ brokerUrl, destination, token }: ActivateConnectionOptions) => {
    client.brokerURL = brokerUrl;
    client.connectHeaders = { token };

    client.onConnect = () => {
        websocketConnectionReadyEvent();
        ensureWsSubscriptions({ destination });
        reconnectAttemptCount = 0;
    };
    client.activate();
};

const incomingMessageHandler = (message: IMessage) => {
    const senderId = message.headers['sender-id'];
    const senderSessionId = message.headers[SENDER_SESSOIN_ID_HEADER];
    const msgGrispiId = message.headers[MESSAGE_ID_HEADER];
    const parsedMessage = JSON.parse(message.body);
    parsedMessage.senderId = parseInt(senderId);
    parsedMessage.id = msgGrispiId; //override database id with msgGrispiId as db id is useless for us
    delete parsedMessage.msgGrispiId;

    if (isMessageSentByMe(parsedMessage)) return;

    if ('code' in parsedMessage) {
        if (parsedMessage.code === SERVER_MESSAGE_CODE.CHAT_SESSION_CLOSED) {
            // TODO: chat-module, remove chatSessionId info from app wherever necessary
            unSubscribeFromSubscription(message.headers['destination']);
            return;
        }
    } else {
        // incoming regular message from a customer
        const chatSessionId = parseChatSessionIdFromSubscription(message.headers['destination']);

        if (chatSessionId && chatSessionIdsOfOpenTabs.includes(chatSessionId)) {
            /**
             * tab with associated this chatSessionId will catch this event
             */
            window.dispatchEvent(
                new CustomEvent(`${InternalEventType.INCOMING_MESSAGE}-${chatSessionId}`, {
                    detail: parsedMessage
                })
            );
        } else {
            /**
             * ChatMessagesUpdates component will catch this event
             */
            window.dispatchEvent(
                new CustomEvent(InternalEventType.INCOMING_MESSAGE, {
                    detail: { message: parsedMessage, chatSessionId }
                })
            );
        }
        message.ack({
            [MESSAGE_ID_HEADER]: parsedMessage.id,
            [SENDER_SESSOIN_ID_HEADER]: senderSessionId
            //TODO send something for agent to differentiate chat sessions
            // maybe chatSessionId
        });
    }
};

const incomingUpdateMessageHandler = (message: IMessage) => {
    const parsedMessage = JSON.parse(message.body);
    if (parsedMessage.code === SERVER_MESSAGE_CODE.INVALID_CHAT_SESSION) {
        //! should have chatSessionId info in message body
        // unSubscribeFromAllSubscriptions(false);
        // unSubscribeFromSubscription(parseChatSessionIdFromSubscription(parsedMessage.chatSessionId));
    } else if (parsedMessage.code === SERVER_MESSAGE_CODE.INVALID_MESSAGE) {
        //TODO what should we do?
        console.error('INVALID_MESSAGE', parsedMessage, message);
    } else if (parsedMessage.code === SERVER_MESSAGE_CODE.MESSAGE_RECEIVED) {
        myMessageReceivedEvent(parsedMessage.msgGrispiId, message.headers['gcsi']);
    } else if (parsedMessage.code === SERVER_MESSAGE_CODE.MESSAGE_SEEN) {
        myMessageSeenEvent(parsedMessage.msgGrispiId, parsedMessage.seenAt, message.headers['gcsi']);
    } else if (parsedMessage.code === SERVER_MESSAGE_CODE.NOT_AUTHORIZED) {
        //! should have chatSessionId info in message body
        // unSubscribeFromSubscription(parseChatSessionIdFromSubscription(parsedMessage.chatSessionId));
        // unSubscribeFromAllSubscriptions(false);
    }
};

/**
 * Ensures ws subscription to channels (any chat channels & updates channel).
 * If any or all of them are not subscribed then this function resubscribe to relevant channels.
 * Note that this function is a low leve ws function and does nothing about resuming chat.
 * @param destination chat channel destination. (no need for updates channel destination as it's a fixed string)
 */
const ensureWsSubscriptions = ({ destination }: Pick<ActivateConnectionOptions, 'destination'>) => {
    const chatSessionId = parseChatSessionIdFromSubscription(destination);

    if (destination !== UPDATES_QUEUE_DESTINATION && !subscriptions[destination]) {
        subscriptions[destination] = client.subscribe(destination, incomingMessageHandler, {
            ack: 'client-individual'
        });
    }

    if (!subscriptionToUpdates) {
        subscriptionToUpdates = client.subscribe(UPDATES_QUEUE_DESTINATION, incomingUpdateMessageHandler);
    }

    const chatPluginId = getChatPlugin()?.pluginId;
    getPluginStore().setChatPluginError(chatPluginId, null);

    window.dispatchEvent(new CustomEvent(`${InternalEventType.SUBSCRIBED_TO_CHAT}-${chatSessionId}`));

    window.dispatchEvent(new CustomEvent(InternalEventType.SUBSCRIBED_TO_CHAT));
};
function sendMessageOverWs(destination: string, message: WsMessage, chatSessionId: string): void {
    if (!message) {
        console.error('connection sendMessageOverWs', 'mandatory "message" parameter is missing!');
        return;
    }

    if (!destination) {
        console.error('connection sendMessageOverWs', 'mandatory "destination" parameter is missing!');
        return;
    }

    if (!chatSessionId) {
        console.error('connection sendMessageOverWs', 'mandatory "chatSessionId" parameter is missing!');
        return;
    }

    //FIXME check connection, subscription. if closed, call resume chat
    const receiptId = message.receiptId!;

    try {
        client.watchForReceipt(receiptId, (frame) => {
            // Having a receiptId means that our message is received by the server

            // Implementation note:
            // here receiptId === msgGrispiId
            const receiptId = frame.headers['receipt-id'];
            myMessageSentEvent(receiptId, chatSessionId);
        });
        client.publish({
            destination,
            headers: { receipt: receiptId },
            body: JSON.stringify(message)
        });
        return;
    } catch (error) {
        console.error(error);
        //FIXME show user that the message is not sent
    }
}

const subscribeChat = (options = {} as ChatSubscriptionOptions) => {
    if (!options.brokerUrl || !options.chatSessionId) {
        console.error('subscribeChat has corrupted options:', options);
        return;
    }

    const destination = `/exchange/${options.tenantId}/${options.tenantId}.${options.chatSessionId}`;

    if (client.connected) {
        ensureWsSubscriptions({ destination });
    } else {
        activateConnection({
            brokerUrl: options.brokerUrl,
            destination,
            token: options.token
        });
    }
};

const sendMessage = (message: WsMessage, destination: string, chatSessionId: string) => {
    if (!destination) {
        console.error('sendMessage destination is not set!');
        return;
    }

    message.receiptId = uuid();

    if (message.senderId !== WELCOME_MESSAGE_SENDER_ID) {
        // Nasty workaround to ignore welcome message as it's already attached to the UI manually

        window.dispatchEvent(
            new CustomEvent(`${InternalEventType.INCOMING_MESSAGE}-${chatSessionId}`, {
                detail: { id: Date.now() * -1, ...message }
            })
        );
    }
    sendMessageOverWs(destination, message, chatSessionId);
};

const endChatSession = (chatSessionId: string) => {
    const destination = `/app/chat/${getTenantId()}/${chatSessionId}/close`;

    if (subscriptions[destination]) {
        client.publish({
            destination,
            headers: {},
            body: chatSessionId
        });
    }
};

const disconnectChat = async () => {
    reconnectAttemptCount = RECONNECT_ATTEMPT_LIMIT;
    client.forceDisconnect();
    await client.deactivate();
    chatSessionIdsOfOpenTabs = [];
    subscriptions = {};
    subscriptionToUpdates = undefined;
};

export {
    addTabRelatedChatSessionId,
    disconnectChat,
    endChatSession,
    parseChatSessionIdFromSubscription,
    removeTabRelatedChatSessionId,
    restartWSConnectionWithRefreshedToken,
    sendMessage,
    sendSeen,
    startWSConnection,
    subscribeChat,
    unSubscribeFromSubscriptionByChatSessionId
};
